'use client'

import { useEffect } from 'react';
import type { ResourceConfigProviderProps } from '../interfaces/ResourceConfigProviderProps';
import { ResourceConfigContext } from '../contexts/ResourceConfigContext';
import { pushClientResourceConfig } from '../functions/pushClientResourceConfig';

/**
 * @group Components
 * 
 * @summary
 * <blockquote>Provider that makes ResourceConfig available to client components. Sets the global client resource config so that getResource() works in both server and client components with the same interface.</blockquote>
 * 
 * @example
 * ```tsx
 * <ResourceConfigProvider config={myResourceConfig}>
 *   <App />
 * </ResourceConfigProvider>
 * ```
 * 
 * @param config - The resource configuration to provide to child components
 * @param children - Child components that will have access to the resource config
 */
/**
 * @internal
 */
export function ResourceConfigProvider({ config, children }: ResourceConfigProviderProps) {
  // Push config onto stack when provider mounts, cleanup on unmount
  useEffect(() => {
    const cleanup = pushClientResourceConfig(config);
    return cleanup;
  }, [config]);

  return (
    <ResourceConfigContext.Provider value={config}>
      {children}
    </ResourceConfigContext.Provider>
  );
}

